import { useEffect, useMemo, useState } from "react";
import { AlertTriangle, ExternalLink, MessageSquare, RefreshCw, Trash2 } from "lucide-react";
import { Button } from "../../../../components/ui/button";
import { Label } from "../../../../components/ui/label";
import { Card, CardContent, CardHeader, CardTitle } from "../../../../components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../../../../components/ui/select";
import {
  useSlackChannels,
  useSlackIntegration,
  useSlackUsers,
} from "../../../../hooks/QueryHooks";
import useWorkspaceStore from "../../../../store/workspace/workspaceStore";
import VariableChipEditor, { type VariableOption } from "./VariableChipEditor";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type SlackActionData = {
  type: "send_to_slack";
  channel_id?: string;
  slack_user_id?: string;
  message_type?: string;
  message?: string;
  assign_to?: string;
};

type Props = {
  action: SlackActionData;
  onChange: (partial: Partial<SlackActionData>) => void;
  onRemove: () => void;
  variables: VariableOption[];
};

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const SlackActionCard = ({ action, onChange, onRemove, variables }: Props) => {
  const workspace: any = useWorkspaceStore((state) => state.workspace);
  const workspaceId = workspace?.id;
  const [mode, setMode] = useState<"channel" | "direct">(action.message_type === "direct" ? "direct" : "channel");

  /* --- Slack integration check --- */
  const { isError: slackNotConnected, isLoading: slackLoading } = useSlackIntegration({
    workspaceId,
    enabled: !!workspaceId,
    retry: false,
  } as any);

  /* --- Channels & Users --- */
  const {
    mutate: fetchChannels,
    data: channelsResponse,
    isLoading: isLoadingChannels,
  } = useSlackChannels();

  const {
    mutate: fetchUsers,
    data: usersResponse,
    isLoading: isLoadingUsers,
  } = useSlackUsers();

  useEffect(() => {
    if (workspaceId && !slackNotConnected) {
      fetchChannels({ workspace_id: workspaceId, refresh: false });
      fetchUsers({ workspace_id: workspaceId, refresh: false });
    }
  }, [workspaceId, slackNotConnected, fetchChannels, fetchUsers]);

  const channels = useMemo(
    () =>
      (channelsResponse?.data?.channels || []).map((ch: any) => ({
        value: ch.id,
        label: `#${ch.normalised_name || ch.name}`,
      })),
    [channelsResponse],
  );

  const slackUsers = useMemo(
    () =>
      (usersResponse?.data?.users || []).map((u: any) => ({
        value: u.id,
        label: u.name || u.real_name || u.id,
      })),
    [usersResponse],
  );

  /* --- Connected Apps URL --- */
  const connectedAppsUrl = workspaceId
    ? `/home/${workspaceId}/settings/integrations`
    : "#";

  /* --- Slack not connected banner --- */
  if (!slackLoading && slackNotConnected) {
    return (
      <Card className="border-amber-200 bg-amber-50">
        <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
          <CardTitle className="flex items-center gap-2 text-sm">
            <MessageSquare className="h-4 w-4" /> Slack Notification
          </CardTitle>
          <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={onRemove}>
            <Trash2 className="h-3.5 w-3.5" />
          </Button>
        </CardHeader>
        <CardContent>
          <div className="flex items-start gap-3 rounded-md border border-amber-300 bg-white p-3">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-amber-600" />
            <div className="flex-1">
              <p className="text-sm font-medium text-amber-800">Slack is not connected</p>
              <p className="mt-0.5 text-xs text-amber-700">
                Connect your Slack workspace in Connected Apps to enable Slack notifications.
              </p>
              <a
                href={connectedAppsUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-primary hover:underline"
              >
                Open Connected Apps
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
        <CardTitle className="flex items-center gap-2 text-sm">
          <MessageSquare className="h-4 w-4" /> Slack Notification
        </CardTitle>
        <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" onClick={onRemove}>
          <Trash2 className="h-3.5 w-3.5" />
        </Button>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Channel / DM toggle */}
        <div>
          <Label className="text-xs">Send to</Label>
          <div className="mt-1 flex gap-2">
            <Button
              variant={mode === "channel" ? "default" : "outline"}
              size="sm"
              className="h-7 text-xs"
              onClick={() => {
                setMode("channel");
                onChange({ message_type: "channel", slack_user_id: undefined });
              }}
            >
              Channel
            </Button>
            <Button
              variant={mode === "direct" ? "default" : "outline"}
              size="sm"
              className="h-7 text-xs"
              onClick={() => {
                setMode("direct");
                onChange({ message_type: "direct" });
              }}
            >
              Direct Message
            </Button>
          </div>
        </div>

        {/* Channel selector */}
        {mode === "channel" ? (
          <div>
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Label className="text-xs">Channel</Label>
                <Select
                  value={action.channel_id || ""}
                  onValueChange={(v) => onChange({ channel_id: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder={isLoadingChannels ? "Loading..." : "Select channel"} />
                  </SelectTrigger>
                  <SelectContent>
                    {channels.map((ch: any) => (
                      <SelectItem key={ch.value} value={ch.value}>{ch.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="outline"
                size="icon"
                className="h-9 w-9 shrink-0"
                onClick={() => fetchChannels({ workspace_id: workspaceId, refresh: true })}
                title="Refresh channels"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${isLoadingChannels ? "animate-spin" : ""}`} />
              </Button>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Label className="text-xs">Slack User</Label>
                <Select
                  value={action.slack_user_id || ""}
                  onValueChange={(v) => onChange({ slack_user_id: v })}
                >
                  <SelectTrigger className="mt-1">
                    <SelectValue placeholder={isLoadingUsers ? "Loading..." : "Select user"} />
                  </SelectTrigger>
                  <SelectContent>
                    {slackUsers.map((u: any) => (
                      <SelectItem key={u.value} value={u.value}>{u.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="outline"
                size="icon"
                className="h-9 w-9 shrink-0"
                onClick={() => fetchUsers({ workspace_id: workspaceId, refresh: true })}
                title="Refresh users"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${isLoadingUsers ? "animate-spin" : ""}`} />
              </Button>
            </div>

            {/* Fallback channel */}
            <div className="flex items-end gap-2">
              <div className="flex-1">
                <Label className="text-xs">Fallback Channel</Label>
                <p className="mb-1 text-[10px] text-muted-foreground">Used if the DM fails</p>
                <Select
                  value={action.channel_id || ""}
                  onValueChange={(v) => onChange({ channel_id: v })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={isLoadingChannels ? "Loading..." : "Select channel"} />
                  </SelectTrigger>
                  <SelectContent>
                    {channels.map((ch: any) => (
                      <SelectItem key={ch.value} value={ch.value}>{ch.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                variant="outline"
                size="icon"
                className="h-9 w-9 shrink-0"
                onClick={() => fetchChannels({ workspace_id: workspaceId, refresh: true })}
                title="Refresh channels"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${isLoadingChannels ? "animate-spin" : ""}`} />
              </Button>
            </div>
          </div>
        )}

        {/* Message */}
        <div>
          <Label className="text-xs">Message</Label>
          <p className="mb-1 text-[10px] text-muted-foreground">
            Use variables to include account data in the message.
          </p>
          <VariableChipEditor
            value={action.message || ""}
            onChange={(msg) => onChange({ message: msg })}
            variables={variables}
            placeholder="e.g. Account {{accounts.name}} needs attention..."
            rows={3}
          />
        </div>
      </CardContent>
    </Card>
  );
};

export default SlackActionCard;
